import { useEffect, useState } from "react";
import { Body, SectionHeader } from "@sprout/typography";
import Container from "@components/ui/container";
import { TextInput } from "@sprout/text_input";
import { Checkbox, Option as CheckboxOption } from "@sprout/checkbox";
import BackButton from "@components/back_button.tsx";

import useTranslation from "@hooks/use_translation";
import useGoBack from "@/hooks/use_go_back";
import useProfile from "@/queries/use_profile";
import { COUNTRIES } from "@/lib/countries";
import { US_STATES } from "@/lib/us_states";
import { useShowFlash } from "@sprout/flash.tsx";
import { type Option, SingleSelect } from "@sprout/select";
import axios, { isAxiosError } from "axios";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";

const DoNotSell = () => {
  const { t } = useTranslation("user");
  const showFlash = useShowFlash();
  const goBack = useGoBack();
  const { data } = useProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState<Option | null>(null);
  const [state, setState] = useState<Option | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const CHECKBOX_OPTIONS = [
    {
      label: t("doNotSell.checkboxLabel"),
      value: "consent",
    },
  ];

  const findByValue = (options: Option[], value: string | null | undefined): Option | null => {
    if (!value) {
      return null;
    }

    const option = options.find((opt) => opt.value === value);
    return option || null;
  };

  useEffect(() => {
    if (data) {
      setFirstName(data.firstName || "");
      setLastName(data.lastName || "");
      setEmail(data.email || "");
      setChecked(Boolean(data.doNotSell));
      setCountry(findByValue(COUNTRIES, data.doNotSell?.country));
      setState(findByValue(US_STATES, data.doNotSell?.state));
    }
  }, [data]);

  const isUSA = country?.value === "united_states";
  const isValid =
    firstName.trim() && lastName.trim() && email.trim() && country && (!isUSA || state) && checked;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!data?.paths.doNotSellPath || !isValid) {
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(data.paths.doNotSellPath, {
        country: country.value,
        state: state?.value || null,
      });

      showFlash(t("doNotSell.success"), "success");
      goBack();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 429) {
        showFlash(t("doNotSell.tooManyAttempts"), "error");
      } else {
        showFlash(t("doNotSell.error"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSetCountry = (value: Option | null) => {
    setCountry(value);
    setState(null);
  };

  const onCheckboxChange = (selected: CheckboxOption | CheckboxOption[]) => {
    if (Array.isArray(selected)) {
      setChecked(selected.some((opt) => opt.value === "consent"));
    } else {
      setChecked(selected.value === "consent");
    }
  };

  return (
    <Container>
      <BackButton href="/profile" />

      <div className="flex flex-col gap-4">
        <SectionHeader as="h1">{t("doNotSell.title")}</SectionHeader>
        <Body>{t("doNotSell.subtitle")}</Body>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col gap-3">
          <div className="w-full md:w-9/12 lg:w-6/12 flex flex-col gap-3">
            <TextInput
              id="first-name"
              label={t("doNotSell.firstName")}
              value={firstName}
              onChange={(event) => setFirstName(event.currentTarget.value)}
              required
            />
            <TextInput
              id="last-name"
              label={t("doNotSell.lastName")}
              value={lastName}
              onChange={(event) => setLastName(event.currentTarget.value)}
              required
            />
            <TextInput
              id="email"
              label={t("doNotSell.email")}
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              required
            />
          </div>
          <SingleSelect
            label={t("doNotSell.country")}
            options={COUNTRIES}
            onChange={onSetCountry}
            value={country}
            required
            clearable
          />
          {isUSA && (
            <SingleSelect
              label={t("doNotSell.state")}
              options={US_STATES}
              onChange={setState}
              value={state}
              required
              clearable
            />
          )}
          <Checkbox
            id="consent"
            options={CHECKBOX_OPTIONS}
            values={checked ? [CHECKBOX_OPTIONS[0].value] : []}
            onSelect={onCheckboxChange}
            isMulti
          />
          <div className="flex justify-end gap-3">
            <SecondaryButton disabled={submitting} onClick={goBack}>
              {t("doNotSell.cancel")}
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={!isValid || submitting}>
              {t("doNotSell.submit")}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </Container>
  );
};

export default DoNotSell;
